// packages/web/src/api/profile.ts
/**
 * Profile API client — update the current user's display name / avatar URL.
 *
 * 19BN Phase D D4: backs the AccountDrawer settings tab. PATCH /auth/profile
 * returns the same `{ user }` shape as /auth/me, so the caller can pass the
 * returned user straight into auth-slice.updateProfile without a second fetch.
 *
 * Pilot scope: avatar is a URL string (no upload). R2 upload tracked as
 * DEBT-19BN-AVATAR-UPLOAD (19BO+).
 *
 * Spec: docs/architecture/account-system-design.md §1.2, §7.1
 */

import { INGEST_URL, IngestApiError, accountHeaders } from '../lib/api-client.js';
import type { IngestErrorResponse } from '../lib/api-client.js';
import type { AuthUser } from '../lib/store/auth-slice.js';

export interface ProfilePatch {
  displayName?: string;
  /** null clears the avatar; a URL string sets it. */
  avatarUrl?: string | null;
}

export interface ProfileResponse {
  user: AuthUser;
}

export const profileApi = {
  /**
   * Update display name / avatar URL. Throws IngestApiError on failure
   * (incl. 400 invalid input, 401 session expired) — the settings UI surfaces
   * the error inline.
   */
  update: (data: ProfilePatch) =>
    patchProfile<ProfileResponse>('/auth/profile', data),
};

async function patchProfile<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'PATCH',
    headers: accountHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }) as IngestErrorResponse);
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}
